// src/components/ClinicWeeklySchedule/ClinicWeeklyScheduleTV.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./ClinicWeeklyScheduleTV.css";
import ThemeConfig from "../../themes/ThemeConfig";
import ThemeDecorations from "../../themes/ThemeDecorations";
import bouquetIcon from "../../assets/bouquet.png";

export default function ClinicWeeklyScheduleTV() {
  const [rooms, setRooms] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [weekDays, setWeekDays] = useState([]);

  // ===== THEME THEO NGÀY =====
  const theme = useMemo(() => ThemeConfig.getActive(), []);

  // ===== HÀM TIỆN ÍCH =====
  const addDays = (d, n) => {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
  };

  const toISO = (d) => {
    if (!d) return "";
    if (typeof d === "string") return d.slice(0, 10);
    const dt = new Date(d);
    return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
  };

  const fmtDMY = (d) =>
    `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}`;

  const dowVN = (d) =>
    ["Chủ Nhật", "Thứ 2", "Thứ 3", "Thứ 4", "Thứ 5", "Thứ 6", "Thứ 7"][d.getDay()];

  // ===== HÔM NAY & NGÀY MAI =====
  useEffect(() => {
    const today = new Date();
    setWeekDays([today, addDays(today, 1)]);
  }, []);

  // ===== LOAD API =====
  useEffect(() => {
    let isMounted = true;

    const fetchAll = async () => {
      try {
        const res = await axios.get("/api/lichlamviec");
        if (!isMounted) return;

        const formatted = (res.data || []).map((x) => ({
          ...x,
          ngayISO: toISO(x.ngay),
          caText: x.buoi === 0 ? "Sáng" : "Chiều",
        }));

        const phongMap = new Map();
        formatted.forEach((x) => {
          if (x.tenPhongKham) {
            phongMap.set(x.id_PhongKham, {
              id: x.id_PhongKham,
              tenPhong: x.tenPhongKham,
            });
          }
        });

        setRooms(Array.from(phongMap.values()));
        setSchedules(formatted);
      } catch (err) {
        console.error("Fetch error", err);
      }
    };

    fetchAll();
    const timer = setInterval(fetchAll, 5 * 60 * 1000);
    return () => {
      isMounted = false;
      clearInterval(timer);
    };
  }, []);

  // ===== MAP DATA =====
  const dataMap = useMemo(() => {
    const map = {};
    rooms.forEach((r) => (map[r.id] = {}));

    schedules.forEach((s) => {
      const rid = s.id_PhongKham;
      if (!map[rid]) map[rid] = {};
      if (!map[rid][s.ngayISO]) map[rid][s.ngayISO] = [];
      map[rid][s.ngayISO].push(s);
    });

    return map;
  }, [rooms, schedules]);

  const pickByCa = (list, ca) => list.filter((s) => s.caText === ca);

  // ===== BODY =====
  const renderBody = () => (
    <table className="tv-table">
      <tbody>
        {rooms.map((r, i) => (
          <tr key={r.id} className={i % 2 === 0 ? "row-even" : "row-odd"}>
            <td className="col-room left-sticky">{r.tenPhong}</td>

            {weekDays.map((d, j) => {
              const iso = toISO(d);
              const list = dataMap[r.id]?.[iso] || [];
              const sang = pickByCa(list, "Sáng");
              const chieu = pickByCa(list, "Chiều");

              const renderCell = (arr) => {
                if (!arr.length) return null;

                const hasChua = arr.some((s) =>
                  (s.ghiChu || "").toLowerCase().includes("chưa")
                );
                const hasNghi = arr.some((s) =>
                  (s.ghiChu || "").toLowerCase().includes("ngh")
                );

                if (hasChua)
                  return <div className="fill-label status-red">Chưa đăng ký</div>;
                if (hasNghi)
                  return <div className="fill-label status-yellow">Nghỉ</div>;

                return (
                  <div className="doc-line">
                    {arr.map((s, idx) => (
                      <div key={idx} className="doc-fullname">
                        {s.tenBS}
                      </div>
                    ))}
                  </div>
                );
              };

              return (
                <React.Fragment key={`${r.id}-${j}`}>
                  <td className="col-cell">{renderCell(sang)}</td>
                  <td className="col-cell">{renderCell(chieu)}</td>
                </React.Fragment>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );

  // ===== CSS VARIABLES theo theme =====
  const themeVars = {
    "--tv-table-header-bg": theme.tableHeaderBg,
    "--tv-table-shift-bg":  theme.tableShiftBg,
    "--tv-table-border":    theme.tableBorder,
    "--tv-col-room-bg":     theme.colRoomBg,
    "--tv-row-odd-bg":      theme.rowOddBg,
    "--tv-header-wrap-bg":  theme.headerWrapBg,
    "--tv-scroll-bg":       theme.scrollBg,
    "--tv-header-color":    theme.headerColor || 'white',
  };

  return (
    <>
      {/* Trang trí theo dịp (Tết / Ngày Thầy Thuốc / ...) */}
      <ThemeDecorations theme={theme} />

      <div
        className={`tv-container ${theme.bgClass}`}
        style={themeVars}
      >
        {/* ── HEADER TIÊU ĐỀ ── */}
        <div
          className="tv-header-top"
          style={{ background: theme.headerBg }}
        >
          <div className="tv-header-main">
            <h1 className="tv-title">{theme.title}</h1>
            <p
              className="tv-subtitle"
              style={{ color: theme.subtitleColor }}
            >
              {theme.decoration === 'women-day' && (
                <img src={bouquetIcon} alt="" className="tv-subtitle-icon" />
              )}
              {theme.subtitle}
              {theme.decoration === 'women-day' && (
                <img src={bouquetIcon} alt="" className="tv-subtitle-icon tv-subtitle-icon-right" />
              )}
            </p>
          </div>
        </div>

        {/* ── HEADER BẢNG (sticky) ── */}
        <div className="tv-header-wrap">
          <table className="tv-table">
            <thead>
              <tr>
                <th rowSpan="2" className="col-room left-sticky">Phòng Khám</th>
                {weekDays.map((d, i) => (
                  <th key={i} colSpan="2" className="col-day">
                    {dowVN(d)} ({fmtDMY(d)})
                  </th>
                ))}
              </tr>
              <tr>
                {weekDays.map((_, i) => (
                  <React.Fragment key={i}>
                    <th className="col-shift">Sáng</th>
                    <th className="col-shift">Chiều</th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>
          </table>
        </div>

        {/* ── BẢNG SCROLL ── */}
        <div className="tv-scroll">
          <div className="marquee-stack">
            <div className="marquee-block">{renderBody()}</div>
            <div className="marquee-block">{renderBody()}</div>
          </div>
        </div>

        {/* ── FOOTER ── */}
        <div
          className="tv-footer"
          style={{
            background: theme.footerBg,
            color: theme.footerColor,
          }}
        >
          <div className="footer-inner">{theme.footerText}</div>
          {theme.footerCopyright && (
            <div className="footer-copyright">{theme.footerCopyright}</div>
          )}
        </div>
      </div>
    </>
  );
}
